/**
 * HR-portal FTUE — layout hook (dashboard + sidebar-nav steps + start logic).
 *
 * Mounted once in `PortalLayout`. Registers the ten steps that don't belong to a
 * single feature page:
 *  - `welcome` / `dashboard-stats` / `dashboard-import-cta` on `/dashboard`
 *  - one `nav-*` step per area, anchored on the sidebar `<Link>` that leads into
 *    it (HR System, People > Import, Stakeholders, Severance, Benefits,
 *    Internal Jobs)
 *  - `wrap-up` back on `/dashboard`
 *
 * The six on-page follow-up steps (orders x2) are registered by the
 * `use-portal-<area>-ftue` hooks on their own pages so each `condition` can close
 * over live page state. All steps share the one `portal-hr-activation` flow.
 *
 * Start rule (per product): auto-start the tour the first time an HR admin is on
 * `/dashboard` with no `portal-hr-*` step recorded server-side — existing admins
 * see it once, new admins on first login, never mid-task. `startFlow` is also
 * re-called on route / entitlement changes so the engine keeps re-picking the
 * next eligible step as the admin moves around (idempotent).
 *
 * Nav steps gate on `isPortalDesktop()` because the sidebar is `hidden md:flex`;
 * below md it is a closed drawer, so the anchor is absent and the step must be
 * skipped rather than left blocking the flow.
 */

import { registerSteps, useFtueStore } from '@/ftue'
import type { FtueStep } from '@/ftue'
import { enableBenefits } from '@/lib/feature-flags'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  PORTAL_HR_STEP_IDS,
  isPortalDesktop,
  onPortalPath,
  portalStep,
} from './flow'

export interface PortalHrFtueArgs {
  /** `features.hris` for the signed-in org (from `useOrgBranding`). */
  hasHris: boolean
  /** `features.severance` for the signed-in org (from `useOrgBranding`). */
  hasSeverance: boolean
  /** Whether People import routes are present for this stakeholder. */
  hasImport?: boolean
  /** Whether the Stakeholders route is present for this stakeholder. */
  hasStakeholders?: boolean
  /** Whether severance mutation/import routes are present for this stakeholder. */
  hasSeveranceManagement?: boolean
  /** Whether the Internal Jobs route is present for this stakeholder. */
  hasInternalJobs?: boolean
}

const DASHBOARD_PATH = '/dashboard'

/** A "click this sidebar item" nudge: shown on desktop only, advanced by clicking
 *  the highlighted `<Link>` (which also navigates), no in-card buttons. */
function navStep(args: {
  id: string
  order: number
  anchorId: string
  body: string
  enabled: () => boolean
}): FtueStep {
  return portalStep({
    id: args.id,
    phase: args.id,
    order: args.order,
    trigger: { type: 'immediate' },
    condition: () => isPortalDesktop() && args.enabled(),
    anchorId: args.anchorId,
    placement: 'right',
    dismissOnAction: true,
    // The anchor click also navigates. Leave enough time for the destination
    // page to register its steps and anchors before choosing the next one.
    advanceDelayMs: 320,
    hideSkip: true,
    content: { body: args.body },
  })
}

export function usePortalHrFtue({
  hasHris,
  hasSeverance,
  hasImport = true,
  hasStakeholders = true,
  hasSeveranceManagement = true,
  hasInternalJobs = true,
}: PortalHrFtueArgs): void {
  const { pathname } = useLocation()
  const hydrated = useFtueStore((s) => s.hydrated)
  const activeFlow = useFtueStore((s) => s.activeFlow)

  // Live entitlement flags for the step `condition`s (registered once below, so
  // the closures must read current values rather than the mount-time snapshot).
  const gatesRef = useRef({
    hasHris,
    hasSeverance,
    hasImport,
    hasStakeholders,
    hasSeveranceManagement,
    hasInternalJobs,
  })
  gatesRef.current = {
    hasHris,
    hasSeverance,
    hasImport,
    hasStakeholders,
    hasSeveranceManagement,
    hasInternalJobs,
  }

  // Register the layout-owned steps once. Registration is idempotent (keyed by
  // id) and does not itself trigger evaluation.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.welcome,
        phase: PORTAL_HR_STEP.welcome,
        order: PORTAL_HR_ORDER.welcome,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(DASHBOARD_PATH),
        anchorId: PORTAL_HR_ANCHOR.dashboardHero,
        placement: 'bottom',
        // ESC on the very first card reads as "no thanks" to the whole tour, so
        // this one step exits the flow; every later step only skips itself.
        skipScope: 'flow',
        content: {
          body: "Welcome to your Pelgo HR portal. Here's a quick tour of getting your organization set up.",
          dismissLabel: 'Start tour',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.dashboardStats,
        phase: PORTAL_HR_STEP.dashboardStats,
        order: PORTAL_HR_ORDER.dashboardStats,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(DASHBOARD_PATH),
        anchorId: PORTAL_HR_ANCHOR.dashboardStats,
        placement: 'bottom',
        hideSkip: true,
        hideDismiss: true,
        autoCompleteMs: 5500,
        content: {
          body: 'Your headcount, onboarding progress, and recent imports stay in view here.',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.dashboardImportCta,
        phase: PORTAL_HR_STEP.dashboardImportCta,
        order: PORTAL_HR_ORDER.dashboardImportCta,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(DASHBOARD_PATH) && gatesRef.current.hasImport,
        anchorId: PORTAL_HR_ANCHOR.dashboardImportCta,
        placement: 'bottom',
        dismissOnAction: true,
        content: {
          body: 'Start by bringing your people in. You can connect your HR system or import a CSV.',
          dismissLabel: 'Got it',
        },
      }),
      navStep({
        id: PORTAL_HR_STEP.navHris,
        order: PORTAL_HR_ORDER.navHris,
        anchorId: PORTAL_HR_ANCHOR.navHris,
        enabled: () => gatesRef.current.hasHris,
        body: 'Connect your HR system here, like BambooHR, Workday or ADP, so people, org chart, and roles sync automatically.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navImport,
        order: PORTAL_HR_ORDER.navImport,
        anchorId: PORTAL_HR_ANCHOR.navImport,
        enabled: () => gatesRef.current.hasImport,
        body: 'No HR system to connect? Import your people from a CSV here.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navPeople,
        order: PORTAL_HR_ORDER.navPeople,
        anchorId: PORTAL_HR_ANCHOR.navPeople,
        enabled: () => true,
        body: 'Once people are in, come back here to search, filter, and manage them.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navStakeholders,
        order: PORTAL_HR_ORDER.navStakeholders,
        anchorId: PORTAL_HR_ANCHOR.navStakeholders,
        enabled: () => gatesRef.current.hasStakeholders,
        body: 'Add your IT, Payroll, Legal, Benefits, and Security contacts. Each gets their own portal login with the tasks assigned to them.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navSeverance,
        order: PORTAL_HR_ORDER.navSeverance,
        anchorId: PORTAL_HR_ANCHOR.navSeveranceImport,
        enabled: () =>
          enableBenefits &&
          gatesRef.current.hasSeverance &&
          gatesRef.current.hasSeveranceManagement,
        body: 'Import the negotiated severance terms for affected employees here.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navSeveranceElections,
        order: PORTAL_HR_ORDER.navSeveranceElections,
        anchorId: PORTAL_HR_ANCHOR.navSeveranceElections,
        enabled: () => enableBenefits && gatesRef.current.hasSeverance,
        body: 'Track how employees are responding to their severance elections here.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navExitGuide,
        order: PORTAL_HR_ORDER.navExitGuide,
        anchorId: PORTAL_HR_ANCHOR.navExitGuide,
        enabled: () => true,
        body: 'Upload your RA or exit-guide documents here. Employees get answers drawn from them in benefits chat.',
      }),
      navStep({
        id: PORTAL_HR_STEP.navInternalJobs,
        order: PORTAL_HR_ORDER.navInternalJobs,
        anchorId: PORTAL_HR_ANCHOR.navInternalJobs,
        enabled: () => gatesRef.current.hasInternalJobs,
        body: "Post internal roles here. Pelgo matches them to employees' enhanced profiles by department and skills.",
      }),
      portalStep({
        id: PORTAL_HR_STEP.wrapUp,
        phase: PORTAL_HR_STEP.wrapUp,
        order: PORTAL_HR_ORDER.wrapUp,
        trigger: { type: 'immediate' },
        condition: () => onPortalPath(DASHBOARD_PATH),
        anchorId: PORTAL_HR_ANCHOR.dashboardHero,
        placement: 'bottom',
        hideSkip: true,
        hideDismiss: true,
        autoCompleteMs: 5200,
        content: {
          body: "You're set. Revisit any section from the sidebar whenever you need it.",
        },
      }),
    ])
  }, [])

  // Start / resume the tour, and keep the engine re-picking the next eligible
  // step as the route and entitlements change (`startFlow` is idempotent):
  //  - fresh admin lands on /dashboard with nothing recorded -> start it
  //  - already enrolled (saw or is mid-`welcome`) -> resume on any portal page
  //  - every step recorded -> do nothing
  // biome-ignore lint/correctness/useExhaustiveDependencies: entitlement flags intentionally re-trigger evaluation; live state is read via getState()
  useEffect(() => {
    if (!hydrated) return
    if (activeFlow && activeFlow !== PORTAL_HR_ACTIVATION_FLOW) return
    const store = useFtueStore.getState()
    const anySeen = PORTAL_HR_STEP_IDS.some((id) => store.seen.has(id))
    const allSeen = PORTAL_HR_STEP_IDS.every((id) => store.seen.has(id))
    const enrolled =
      store.seen.has(PORTAL_HR_STEP.welcome) || activeFlow === PORTAL_HR_ACTIVATION_FLOW
    if (!allSeen && ((pathname === DASHBOARD_PATH && !anySeen) || enrolled)) {
      store.startFlow(PORTAL_HR_ACTIVATION_FLOW)
    }
  }, [
    pathname,
    hydrated,
    activeFlow,
    hasHris,
    hasSeverance,
    hasImport,
    hasStakeholders,
    hasSeveranceManagement,
    hasInternalJobs,
  ])
}
